import { LoginCredentials, RegisterData, PasswordChangeData } from '../../schemas/auth';
import { User, RegisterResponse } from '../../schemas/auth';
import api from '../../lib/api';
import type { AxiosResponse } from '../../lib/api';

// Auth API with generic type support
export const authAPI = {
  login: (
    credentials: LoginCredentials
  ): Promise<
    AxiosResponse<{ access_token: string; refresh_token: string; token_type: string }>
  > => {
    // Use URLSearchParams for OAuth2 compatible login (works better than FormData)
    const params = new URLSearchParams();
    params.append('username', credentials.email); // OAuth2 expects 'username' field
    params.append('password', credentials.password);

    return api.post<{
      access_token: string;
      refresh_token: string;
      token_type: string;
    }>('/api/v1/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      // Skip global error handling for login - errors are displayed inline
      silent: true,
    });
  },

  register: (userData: RegisterData): Promise<AxiosResponse<RegisterResponse>> =>
    api.post<RegisterResponse>('/api/v1/auth/register', userData),

  getCurrentUser: (): Promise<AxiosResponse<User>> => api.get<User>('/api/v1/auth/me'),

  logout: (): Promise<
    AxiosResponse<{
      message: string;
    }>
  > => api.post<{ message: string }>('/api/v1/auth/logout'),

  refreshToken: (
    refreshToken: string
  ): Promise<AxiosResponse<{ access_token: string; refresh_token: string; token_type: string }>> =>
    api.post<{
      access_token: string;
      refresh_token: string;
      token_type: string;
    }>('/api/v1/auth/refresh', { refresh_token: refreshToken }),

  updateProfile: (
    userData: Pick<User, 'full_name' | 'bio' | 'avatar_url'>
  ): Promise<AxiosResponse<User>> => api.put<User>('/api/v1/auth/profile', userData),

  uploadAvatar: (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse<{ avatar_url: string }>> => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post<{ avatar_url: string }>('/api/v1/auth/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    } as any);
  },

  changePassword: (passwordData: PasswordChangeData): Promise<AxiosResponse<{ message: string }>> =>
    api.post<{ message: string }>('/api/v1/auth/change-password', passwordData),
};
